import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE] = CHART_COLORS;

const AXIS = { left: 36, right: 524, y: 118 };
/** The scale runs from 10³ J to 10²⁷ J: 24 decades. */
const LOW = 3;
const HIGH = 27;

/** One decimal place: logarithms can differ in the last digit between Node and a browser. */
const round = (value: number): number => Math.round(value * 10) / 10;

function position(joules: number): number {
  const decade = Math.min(Math.max(Math.log10(joules), LOW), HIGH);
  return round(
    AXIS.left + ((decade - LOW) / (HIGH - LOW)) * (AXIS.right - AXIS.left)
  );
}

function anchorFor(x: number): "start" | "middle" | "end" {
  if (x > AXIS.right - 80) {
    return "end";
  }
  return x < AXIS.left + 60 ? "start" : "middle";
}

const TICK_LABELS: Record<number, string> = {
  3: "10³",
  6: "10⁶",
  9: "10⁹",
  12: "10¹²",
  15: "10¹⁵",
  18: "10¹⁸",
  21: "10²¹",
  24: "10²⁴",
  27: "10²⁷",
};

export interface Benchmark {
  joules: number;
  label: string;
}

/**
 * A logarithmic energy ruler with familiar yardsticks above and below it and the
 * calculated energy marked on it, so "8.99 × 10¹³ J" lands somewhere you can picture.
 * Values below 1 kJ or above 10²⁷ J sit at the ends of the ruler.
 */
export function EnergyScale({
  id,
  label,
  description,
  joules,
  benchmarks,
  markerText,
}: {
  id: string;
  label: string;
  description: string;
  joules: number;
  benchmarks: readonly Benchmark[];
  markerText: string;
}) {
  const marker = position(joules);
  const decades = [3, 6, 9, 12, 15, 18, 21, 24, 27];
  return (
    <ChartSvg
      description={description}
      height={190}
      id={id}
      label={label}
      width={560}
    >
      <line
        stroke={CHART_INK.axis}
        strokeWidth="2"
        x1={AXIS.left}
        x2={AXIS.right}
        y1={AXIS.y}
        y2={AXIS.y}
      />
      {decades.map((d) => {
        const x = position(10 ** d);
        return (
          <g key={d}>
            <line
              stroke={CHART_INK.axis}
              x1={x}
              x2={x}
              y1={AXIS.y - 4}
              y2={AXIS.y + 4}
            />
            <text
              fill={CHART_INK.muted}
              fontSize="11"
              textAnchor="middle"
              x={x}
              y={AXIS.y + 18}
            >
              {TICK_LABELS[d]}
            </text>
          </g>
        );
      })}
      {benchmarks.map((b, i) => {
        const x = position(b.joules);
        const above = i % 2 === 0;
        return (
          <g key={b.label}>
            <line
              stroke={BLUE}
              strokeWidth="2"
              x1={x}
              x2={x}
              y1={AXIS.y}
              y2={above ? AXIS.y - 26 : AXIS.y + 30}
            />
            <circle cx={x} cy={AXIS.y} fill={BLUE} r={4} />
            <text
              fill={CHART_INK.text}
              fontSize="12"
              textAnchor={x > AXIS.right - 50 ? "end" : "middle"}
              x={x}
              y={above ? AXIS.y - 32 : AXIS.y + 46}
            >
              {b.label}
            </text>
          </g>
        );
      })}
      <polygon
        fill={ORANGE}
        points={`${marker},${AXIS.y - 2} ${marker - 8},${AXIS.y - 20} ${marker + 8},${AXIS.y - 20}`}
      />
      <text
        fill={ORANGE}
        fontSize="14"
        fontWeight="bold"
        textAnchor={anchorFor(marker)}
        x={marker}
        y={AXIS.y - 62}
      >
        {markerText}
      </text>
      <text
        fill={CHART_INK.muted}
        fontSize="12"
        textAnchor="end"
        x={AXIS.right}
        y={AXIS.y + 78}
      >
        J
      </text>
    </ChartSvg>
  );
}
